// src/context/AppContext.js
import React,{
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';

// API Configuration - Use relative paths
const API_BASE_URL = '';
const API_WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
const API_PATH = '/api';

// Create context
export const AppContext = createContext();

// Debounce helper
const debounce = (fn, delay = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// Context provider component
export const AppProvider = ({ children }) => {
  const { apiRequest, isAuthenticated, isAuthLoading } = useAuth();
  const [devices, setDevices] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [microphones, setMicrophones] = useState([]);
  const [audioStreamData, setAudioStreamData] = useState({});
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasFetchedOnce = useRef(false);

  // Update audio stream data
  const updateAudioStreamData = useCallback((micId, data) => {
    if (!micId) return;
    
    setAudioStreamData(prev => ({
      ...prev,
      [micId]: data
    }));
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    try {
      if(data instanceof Blob){
        data.arrayBuffer().then(buffer => {
          const view = new DataView(buffer);
    
          const headerLength = view.getUint32(0); // first 4 bytes
          const headerBytes = new Uint8Array(buffer, 4, headerLength);
          const headerString = new TextDecoder().decode(headerBytes);
          const header = JSON.parse(headerString);
    
          const audioBuffer = buffer.slice(4 + headerLength); // raw PCM
          updateAudioStreamData(header.micId, audioBuffer);
          // console.log("Received audio from", header.micId, header);
        });
        return;
      }
      const parsedData = data;

      if (parsedData.type === 'deviceUpdate') {
        // For the new device format (array of devices with embedded status)
        setDevices(prevDevices => {
          return prevDevices.map(device => {
            if (device.id === parsedData.deviceId) {
              // Update controls with new values if they exist
              const updatedControls = device.controls ? 
                device.controls.map(control => {
                  if (parsedData.status[control.property] !== undefined) {
                    return {
                      ...control,
                      value: parsedData.status[control.property]
                    };
                  }
                  return control;
                }) : 
                device.controls;
              
              // Update sensors with new values if they exist
              const updatedSensors = device.sensors ?
                device.sensors.map(sensor => {
                  if (parsedData.status[sensor.property] !== undefined) {
                    return {
                      ...sensor,
                      value: parsedData.status[sensor.property]
                    };
                  }
                  return sensor;
                }) :
                device.sensors;
              
              // Return updated device
              return {
                ...device,
                controls: updatedControls,
                sensors: updatedSensors,
                linkquality: parsedData.status.linkquality || device.linkquality,
                lastUpdated: parsedData.status.lastUpdated || new Date().toISOString()
              };
            }
            return device;
          });
        });
      } else if (parsedData.type === 'sceneUpdate') {
        setScenes((prev) =>
          prev.map((scene) =>
            scene.id === parsedData.sceneId
              ? { ...scene, ...parsedData.updates }
              : scene
          )
        );
      } else if (parsedData.type === 'microphoneUpdate') {
        // Handle microphone updates (e.g., when a new microphone is added or updated)
        setMicrophones(prevMicrophones => {
          const index = prevMicrophones.findIndex(mic => mic.id === parsedData.microphoneId);
          if (index === -1) {
            // New microphone
            return [...prevMicrophones, parsedData.microphone];
          } else {
            // Update existing microphone
            return prevMicrophones.map(mic => 
              mic.id === parsedData.microphoneId ? parsedData.microphone : mic
            );
          }
        });
      } else if (parsedData.type === 'systemNotification') {
        switch (parsedData.level) {
          case 'error':
            toast.error(parsedData.message);
            break;
          case 'warning':
            toast.warning(parsedData.message);
            break;
          case 'success':
            toast.success(parsedData.message);
            break;
          default:
            toast.info(parsedData.message);
        }
      } 
      // Handle smart lock updates
      else if (parsedData.type === 'lockDiscovered') {
        // Add or update lock in the list
        setSmartLocks(prevLocks => {
          const existingLockIndex = prevLocks.findIndex(lock => lock.lockMac === parsedData.data.lockMac);
          if (existingLockIndex >= 0) {
            // Update existing lock
            const updatedLocks = [...prevLocks];
            updatedLocks[existingLockIndex] = {...updatedLocks[existingLockIndex], ...parsedData.data};
            return updatedLocks;
          } else {
            // Add new lock
            return [...prevLocks, parsedData.data];
          }
        });
      } else if (parsedData.type === 'lockStatusChanged') {
        // Update lock status
        if (parsedData.data && parsedData.data.lockMac && parsedData.data.status) {
          setSmartLocks(prevLocks => {
            return prevLocks.map(lock => {
              if (lock.lockMac === parsedData.data.lockMac) {
                const updatedLock = {...lock};
                
                // Update lock status based on the status message
                if (parsedData.data.status === 'locked') {
                  updatedLock.isLocked = true;
                } else if (parsedData.data.status === 'unlocked') {
                  updatedLock.isLocked = false;
                } else if (parsedData.data.status === 'connected') {
                  updatedLock.isConnected = true;
                } else if (parsedData.data.status === 'disconnected') {
                  updatedLock.isConnected = false;
                } else if (parsedData.data.status === 'initialized') {
                  updatedLock.initialized = true;
                }
                
                // Update lock alias if provided
                if (parsedData.data.lockAlias) {
                  updatedLock.lockAlias = parsedData.data.lockAlias;
                }
                
                return updatedLock;
              }
              return lock;
            });
          });
          
          // Show notification for important status changes
          if (['locked', 'unlocked'].includes(parsedData.data.status)) {
            toast.success(`Lock ${parsedData.data.lockAlias || parsedData.data.lockMac} is now ${parsedData.data.status}`);
          }
        }
      } else if (parsedData.type === 'locksStatus') {
        // Update entire locks list
        if (parsedData.data && Array.isArray(parsedData.data)) {
          setSmartLocks(parsedData.data);
        }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, [updateAudioStreamData]);

  // State for configuration
  const [config, setConfig] = useState({});
  
  // State for smart locks
  const [smartLocks, setSmartLocks] = useState([]);

  const getToken = () => localStorage.getItem('authToken'); // or from Redux/context
  
  // Initialize WebSocket only when authenticated
  const { isConnected } = useWebSocket(
    isAuthenticated ? `${API_WS_URL}/ws/devices` : null,
    isAuthenticated ? handleWebSocketMessage : null,
    isAuthenticated ? getToken : null
  );

  // Reset all data when user logs out
  const resetData = useCallback(() => {
    setDevices([]);
    setScenes([]);
    setMicrophones([]);
    setAudioStreamData({});
    setConfig({});
    setSmartLocks([]);
    setIsInitialFetchDone(false);
    setFetchError(null);
    setIsLoading(false);
    hasFetchedOnce.current = false;
  }, []);

  // Initial fetch - only when authenticated and not loading
  useEffect(() => {
    // Don't fetch if auth is still loading or user is not authenticated
    if (isAuthLoading || !isAuthenticated) {
      if (!isAuthenticated && !isAuthLoading) {
        // User is definitely not authenticated, reset data
        resetData();
      }
      return;
    }

    // Don't fetch if already fetched once
    if (hasFetchedOnce.current) return;

    const initializeFetch = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);

        await Promise.all([
          fetchDevices(), 
          fetchScenes(), 
          fetchMicrophones(), 
          fetchConfig(),
          fetchSmartLocks()
        ]);
        setIsInitialFetchDone(true);
        hasFetchedOnce.current = true;
      } catch (error) {
        console.error('Initial fetch failed:', error);
        setFetchError(error);
        // Only show toast error if user is still authenticated (avoid showing errors during logout)
        if (isAuthenticated) {
          toast.error('Failed to load initial data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeFetch();
  }, [isAuthenticated, isAuthLoading]); // Dependencies include auth state

  // Fetch devices - using the new API format where each gang is a separate device
  const fetchDevices = useCallback(async () => {
    if (!isAuthenticated) return [];
    
    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/devices`);

      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }
      
      // Store devices as an array (new format)
      setDevices(data);
      
      return data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      if (isAuthenticated) {
        toast.error('Failed to load devices');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Fetch scenes
  const fetchScenes = useCallback(async () => {
    if (!isAuthenticated) return [];
    
    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/scenes`);

      if (!response.ok) {
        throw new Error('Failed to fetch scenes');
      }

      setScenes(data);
      return data;
    } catch (error) {
      console.error('Error fetching scenes:', error);
      if (isAuthenticated) {
        toast.error('Failed to load scenes');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Fetch microphones
  const fetchMicrophones = useCallback(async () => {
    if (!isAuthenticated) return [];
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/microphones`);

      if (!response.ok) {
        throw new Error('Failed to fetch microphones');
      }

      setMicrophones(data);
      return data;
    } catch (error) {
      console.error('Error fetching microphones:', error);
      if (isAuthenticated) {
        toast.error('Failed to load microphones');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Control device - updated for the new API
  const controlDevice = useCallback(async (deviceId, property, value, parentId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/devices/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId,
          property,
          value,
          parentId
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to control device');
      }
      
      const result =data;
      
      // Update the local device state optimistically
      setDevices(prevDevices => {
        return prevDevices.map(device => {
          if (device.id === deviceId) {
            // Find the control to update
            const updatedControls = device.controls ? 
              device.controls.map(control => {
                if (control.property === property) {
                  return {
                    ...control,
                    value: value
                  };
                }
                return control;
              }) : 
              device.controls;
            
            return {
              ...device,
              controls: updatedControls,
              lastUpdated: new Date().toISOString()
            };
          }
          return device;
        });
      });
      
      return result;
    } catch (error) {
      console.error('Control device error:', error);
      toast.error(`Failed to control device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Activate scene
  const activateScene = useCallback(async (sceneId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    console.log("activateScene "+sceneId)
    try {
      const {response, data}= await apiRequest(
        `${API_BASE_URL}${API_PATH}/scenes/${sceneId}/activate`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to activate scene');
      }

      return data;
    } catch (error) {
      console.log('Error activating scene:', error);
      toast.error(`Failed to activate scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Update microphone metadata
  const updateMicrophoneMetadata = useCallback(async (micId, metadata) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/microphones/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          micId,
          ...metadata
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update microphone metadata');
      }
      
      const updatedMic = data;
      
      // Update microphones state
      setMicrophones(prev => 
        prev.map(mic => mic.id === micId ? updatedMic : mic)
      );
      
      toast.success(`Microphone ${metadata.name || micId} updated successfully`);
      return updatedMic;
    } catch (error) {
      console.error('Update microphone metadata error:', error);
      toast.error(`Failed to update microphone: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Add a new microphone
  const addMicrophone = useCallback(async (microphoneData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/microphones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(microphoneData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add microphone');
      }
      
      const newMicrophone = data;
      
      // Update microphones state
      setMicrophones(prev => [...prev, newMicrophone]);
      
      toast.success(`Microphone ${newMicrophone.name} added successfully`);
      return newMicrophone;
    } catch (error) {
      console.error('Add microphone error:', error);
      toast.error(`Failed to add microphone: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Remove a microphone
  const removeMicrophone = useCallback(async (micId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/microphones/${micId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove microphone');
      }
      
      // Update microphones state
      setMicrophones(prev => prev.filter(mic => mic.id !== micId));
      
      // Also clear any audio stream data for this microphone
      setAudioStreamData(prev => {
        const newData = { ...prev };
        delete newData[micId];
        return newData;
      });
      
      toast.success('Microphone removed successfully');
      return true;
    } catch (error) {
      console.error('Remove microphone error:', error);
      toast.error(`Failed to remove microphone: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Debounced refresh methods
  const refreshDevices = useCallback(debounce(() => fetchDevices(), 500), [fetchDevices]);
  const refreshScenes = useCallback(debounce(() => fetchScenes(), 500), [fetchScenes]);
  const refreshMicrophones = useCallback(debounce(() => fetchMicrophones(), 500), [fetchMicrophones]);

  // Update device metadata
  const updateDeviceMetadata = useCallback(async (deviceId, metadata) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/devices/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId,
          ...metadata
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update device metadata');
      }
      
      // The API returns the complete updated device list
      const updatedDevices = data;
      
      // Update devices state with the new list
      setDevices(updatedDevices);
      
      toast.success(`Device ${metadata.name || deviceId} updated successfully`);
      return updatedDevices;
    } catch (error) {
      console.error('Update device metadata error:', error);
      toast.error(`Failed to update device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Create a new scene
  const createScene = useCallback(async (sceneData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sceneData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create scene');
      }
      
      const newScene = data;
      
      // Update scenes state
      setScenes(prev => [...prev, newScene]);
      
      toast.success(`Scene "${newScene.name}" created successfully`);
      return newScene;
    } catch (error) {
      console.error('Create scene error:', error);
      toast.error(`Failed to create scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Update a scene
  const updateScene = useCallback(async (sceneId, updates) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      toast.success(`Scene "${updatedScene.name}" updated successfully`);
      return updatedScene;
    } catch (error) {
      console.error('Update scene error:', error);
      toast.error(`Failed to update scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Delete a scene
  const deleteScene = useCallback(async (sceneId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete scene');
      }
      
      // Update scenes state
      setScenes(prev => prev.filter(scene => scene.id !== sceneId));
      
      toast.success('Scene deleted successfully');
      return true;
    } catch (error) {
      console.error('Delete scene error:', error);
      toast.error(`Failed to delete scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Add an action to a scene
  const addSceneAction = useCallback(async (sceneId, action) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/add-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add action to scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Add scene action error:', error);
      toast.error(`Failed to add action to scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Update a scene action
  const updateSceneAction = useCallback(async (sceneId, actionIndex, action) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/actions/${actionIndex}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update scene action');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Update scene action error:', error);
      toast.error(`Failed to update scene action: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Remove an action from a scene
  const removeSceneAction = useCallback(async (sceneId, actionIndex) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/actions/${actionIndex}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove action from scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Remove scene action error:', error);
      toast.error(`Failed to remove action from scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    if (!isAuthenticated) return {};
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/config`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }
      
      setConfig(data);
      return data;
    } catch (error) {
      console.error('Error fetching configuration:', error);
      if (isAuthenticated) {
        toast.error('Failed to load configuration');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Fetch specific configuration section
  const fetchConfigSection = useCallback(async (section) => {
    if (!isAuthenticated) return {};
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/config/${section}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${section} configuration`);
      }
      
      // Update only the specific section in the state
      setConfig(prev => ({
        ...prev,
        [section]: data
      }));
      
      return data;
    } catch (error) {
      console.error(`Error fetching ${section} configuration:`, error);
      if (isAuthenticated) {
        toast.error(`Failed to load ${section} configuration`);
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update configuration
  const updateConfig = useCallback(async (configData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config: configData }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update configuration');
      }
      
      const result = data;
      
      if (result.success) {
        // Update local state
        setConfig(prev => ({
          ...prev,
          ...configData
        }));
        
        toast.success('Configuration updated successfully');
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating configuration:', error);
      toast.error(`Failed to update configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update specific configuration section
  const updateConfigSection = useCallback(async (section, sectionData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data}= await apiRequest(`${API_BASE_URL}${API_PATH}/config/${section}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sectionData),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update ${section} configuration`);
      }
      
      const result = data;
      
      if (result.success) {
        // Update local state for the section
        setConfig(prev => ({
          ...prev,
          [section]: {
            ...(prev[section] || {}),
            ...sectionData
          }
        }));
        
        toast.success(`${section} configuration updated successfully`);
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error(`Error updating ${section} configuration:`, error);
      toast.error(`Failed to update ${section} configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Debounced refresh config
  const refreshConfig = useCallback(debounce(() => fetchConfig(), 500), [fetchConfig]);
  
  // Fetch smart locks
  const fetchSmartLocks = useCallback(async () => {
    if (!isAuthenticated) return [];
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch smart locks');
      }
      
      setSmartLocks(data);
      return data;
    } catch (error) {
      console.error('Error fetching smart locks:', error);
      if (isAuthenticated) {
        toast.error('Failed to load smart locks');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Start scanning for smart locks
  const startSmartLockScan = useCallback(async (duration = 15000) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/scan/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ duration }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to start scan');
      }
      
      const result = data;
      
      toast.info(`Scanning for locks for ${duration / 1000} seconds...`);
      
      // After scan completes, refresh the lock list
      setTimeout(() => {
        fetchSmartLocks();
      }, duration + 500); // Add a small buffer
      
      return result;
    } catch (error) {
      console.error('Error starting smart lock scan:', error);
      toast.error('Failed to start scan');
      throw error;
    }
  }, [fetchSmartLocks, apiRequest, isAuthenticated]);
  
  // Stop scanning for smart locks
  const stopSmartLockScan = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/scan/stop`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to stop scan');
      }
      
      return data;
    } catch (error) {
      console.error('Error stopping smart lock scan:', error);
      toast.error('Failed to stop scan');
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Initialize a smart lock
  const initializeSmartLock = useCallback(async (mac, alias) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac, alias }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to initialize lock');
      }
      
      const result = data;
      
      if (result.success) {
        toast.success(`Lock ${alias || mac} initialized successfully`);
        await fetchSmartLocks();
      } else {
        toast.error('Failed to initialize lock');
      }
      
      return result;
    } catch (error) {
      console.error('Error initializing smart lock:', error);
      toast.error(`Failed to initialize lock: ${error.message}`);
      throw error;
    }
  }, [fetchSmartLocks, apiRequest, isAuthenticated]);
  
  // Lock a smart lock
  const lockSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/lock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to lock device');
      }
      
      const result = data;
      
      if (result.success) {
        // Update lock in the local state
        setSmartLocks(prevLocks => 
          prevLocks.map(lock => 
            lock.lockMac === mac ? { ...lock, isLocked: true } : lock
          )
        );
      } else {
        toast.error('Failed to lock device');
      }
      
      return result;
    } catch (error) {
      console.error('Error locking smart lock:', error);
      toast.error(`Failed to lock device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Unlock a smart lock
  const unlockSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to unlock device');
      }
      
      const result = data;
      
      if (result.success) {
        // Update lock in the local state
        setSmartLocks(prevLocks => 
          prevLocks.map(lock => 
            lock.lockMac === mac ? { ...lock, isLocked: false } : lock
          )
        );
      } else {
        toast.error('Failed to unlock device');
      }
      
      return result;
    } catch (error) {
      console.error('Error unlocking smart lock:', error);
      toast.error(`Failed to unlock device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Toggle a smart lock (lock/unlock)
  const toggleSmartLock = useCallback(async (mac, currentStatus) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      if (currentStatus) {
        return await unlockSmartLock(mac);
      } else {
        return await lockSmartLock(mac);
      }
    } catch (error) {
      console.error('Error toggling smart lock:', error);
      throw error;
    }
  }, [lockSmartLock, unlockSmartLock, isAuthenticated]);
  
  // Get smart lock status
  const getSmartLockStatus = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/status`);
      
      if (!response.ok) {
        throw new Error('Failed to get lock status');
      }
      
      const status = data;
      
      // Update lock in the local state
      setSmartLocks(prevLocks => 
        prevLocks.map(lock => 
          lock.lockMac === mac ? { 
            ...lock, 
            isLocked: status.isLocked,
            isConnected: status.isConnected,
            batteryLevel: status.batteryLevel
          } : lock
        )
      );
      
      return status;
    } catch (error) {
      console.error('Error getting smart lock status:', error);
      toast.error(`Failed to get lock status: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update smart lock alias (name)
  const updateSmartLockAlias = useCallback(async (mac, alias) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/alias`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac, alias }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update lock alias');
      }
      
      const result = data;
      
      if (result.success) {
        // Update lock in the local state
        setSmartLocks(prevLocks => 
          prevLocks.map(lock => 
            lock.lockMac === mac ? { ...lock, lockAlias: alias } : lock
          )
        );
        
        toast.success(`Lock name updated to ${alias}`);
      } else {
        toast.error('Failed to update lock name');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating smart lock alias:', error);
      toast.error(`Failed to update lock name: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Set auto lock time
  const setAutoLockTime = useCallback(async (mac, seconds) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/autolock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac, seconds }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to set auto lock time');
      }
      
      const result = data;
      
      if (result.success) {
        // Update lock in the local state
        setSmartLocks(prevLocks => 
          prevLocks.map(lock => 
            lock.lockMac === mac ? { ...lock, autoLockTime: seconds } : lock
          )
        );
        
        toast.success(`Auto-lock set to ${seconds} seconds`);
      } else {
        toast.error('Failed to set auto lock time');
      }
      
      return result;
    } catch (error) {
      console.error('Error setting auto lock time:', error);
      toast.error(`Failed to set auto lock time: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Fetch passcodes for a lock
  const fetchPasscodes = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/passcodes`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch passcodes');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching passcodes:', error);
      toast.error(`Failed to load passcodes: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Add a passcode to a lock
  const addPasscode = useCallback(async (mac, passcode, name, startDate, endDate, type = '1') => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/passcodes/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac, passcode, name, startDate, endDate, type }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add passcode');
      }
      
      const result = data;
      
      if (result.success) {
        toast.success(`Passcode ${name} added successfully`);
      } else {
        toast.error('Failed to add passcode');
      }
      
      return result;
    } catch (error) {
      console.error('Error adding passcode:', error);
      toast.error(`Failed to add passcode: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Delete a passcode from a lock
  const deletePasscode = useCallback(async (mac, id) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/passcodes/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mac, id }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete passcode');
      }
      
      const result = data;
      
      if (result.success) {
        toast.success('Passcode deleted successfully');
      } else {
        toast.error('Failed to delete passcode');
      }
      
      return result;
    } catch (error) {
      console.error('Error deleting passcode:', error);
      toast.error(`Failed to delete passcode: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Debounced refresh smart locks
  const refreshSmartLocks = useCallback(debounce(() => fetchSmartLocks(), 500), [fetchSmartLocks]);

  // Fetch Zigbee2MQTT configuration
  const fetchZigbee2mqttConfig = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch Zigbee2MQTT configuration');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching Zigbee2MQTT configuration:', error);
      toast.error('Failed to load Zigbee2MQTT configuration');
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update Zigbee2MQTT configuration
  const updateZigbee2mqttConfig = useCallback(async (configData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config: configData }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update Zigbee2MQTT configuration');
      }
      
      const result = data;
      
      if (result.success) {
        toast.success('Zigbee2MQTT configuration updated successfully');
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating Zigbee2MQTT configuration:', error);
      toast.error(`Failed to update Zigbee2MQTT configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Restart Zigbee2MQTT service
  const restartZigbee2mqtt = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/restart`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to restart Zigbee2MQTT service');
      }
      
      const result = data;
      
      if (result.success) {
        toast.success('Zigbee2MQTT service restart initiated');
      } else {
        throw new Error(result.message || 'Restart failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error restarting Zigbee2MQTT service:', error);
      toast.error(`Failed to restart Zigbee2MQTT service: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Provide context
  const contextValue = {
    devices,
    scenes,
    microphones,
    audioStreamData,
    config,
    smartLocks,
    isConnected,
    isLoading,
    fetchError,
    isAuthenticated,
    controlDevice,
    activateScene,
    refreshDevices,
    refreshScenes,
    refreshMicrophones,
    refreshConfig,
    updateDeviceMetadata,
    updateMicrophoneMetadata,
    updateAudioStreamData,
    addMicrophone,
    removeMicrophone,
    resetData,
    // Scene management
    createScene,
    updateScene,
    deleteScene,
    addSceneAction,
    updateSceneAction,
    removeSceneAction,
    // Configuration management
    fetchConfig,
    fetchConfigSection,
    updateConfig,
    updateConfigSection,
    // Zigbee2MQTT configuration management
    fetchZigbee2mqttConfig,
    updateZigbee2mqttConfig,
    restartZigbee2mqtt,
    // Smart lock management
    fetchSmartLocks,
    refreshSmartLocks,
    startSmartLockScan,
    stopSmartLockScan,
    initializeSmartLock,
    lockSmartLock,
    unlockSmartLock,
    toggleSmartLock,
    getSmartLockStatus,
    updateSmartLockAlias,
    setAutoLockTime,
    fetchPasscodes,
    addPasscode,
    deletePasscode,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;